import { Inject, Injectable } from '@angular/core';
import { CookieService } from 'ngx-cookie-service';
import 'rxjs/add/observable/fromPromise';
import { Observable } from 'rxjs/Observable';
import { redirectToURL, useDefault } from '../../common/common';
import { SiteURL, SiteURLUtils } from '../../common/site.url.utils';
import { WindowRef } from '../../common/window.provider';
import { IAppConfig } from '../../store/interfaces/IAppConfig';
import { APP_CONFIG } from '../config/app.config';
import { UserStatusType } from '../model/user/user-status-type.model';
import { User } from '../model/user/user.model';
import { UserModelBuilder } from './builders/user-model.builder';

const UHF_NAMESPACE = 'UHF';
const UHF_READY = 'isReady';
const UHF_OPEN_WALLET = 'showWallet';
const UHF_OPEN_ACCOUNT_MANAGEMENT = 'goToAccountManagement';
const IHG_PAGE_NAMESPACE = 'IHG_Page';
const IHG_PAGE_HEADER = 'Header';
const RELOAD_PAGE_OPTIONS = 'setReloadPageOptions';
const PAGE_USER_INFO = 'userInfoPromise';
const UHF_SIGN_STATE_CHANGE_EVENT = 'changeSignState.uhf';
const UHF_LOGIN_CHANGE_EVENT = 'changeSignState.hostedApp';
const UHF_SIGN_IN_AND_EXECUTE = 'signInAndExecute';
const UHF_DO_LOG_IN = 'doLogIn';
const IHG_SSO_TOKEN = 'X-IHG-SSO-TOKEN';
const IHG_DOMAIN = '.ihg.com';
const IHG_CHINA_DOMAIN = '.ihg.com.cn';

@Injectable()
export class UHFInteractionService {
  constructor(@Inject(APP_CONFIG) private appConfig: IAppConfig,
              private _window: WindowRef,
              private _cookieService: CookieService) {}

  isUHFPresent(): boolean {
    return !!this.getUHF();
  }

  getUHF(): boolean {
    return this._window.nativeWindow[UHF_NAMESPACE];
  }

  openWallet(): boolean {
    const uhf = this.getUHF();
    return !!uhf && (typeof uhf[UHF_OPEN_WALLET] === 'function') && uhf[UHF_OPEN_WALLET]();
  }

  openWalletAndExecute(toExecute: any): boolean {
    const uhf = this.getUHF();
    return !!uhf && (typeof uhf[UHF_SIGN_IN_AND_EXECUTE] === 'function') && uhf[UHF_SIGN_IN_AND_EXECUTE](toExecute);
  }

  isUHFReady(): boolean {
    const uhf = this.getUHF();
    return !!uhf && (typeof uhf[UHF_READY] === 'function') && uhf[UHF_READY]();
  }

  openAccountManagement(siteUrl = SiteURL.ACCOUNT_MANAGEMENT) {
    const page = this._window.nativeWindow[IHG_PAGE_NAMESPACE];
    const ssoToken = this._cookieService.get(IHG_SSO_TOKEN);
    const siteUrlValue = SiteURLUtils.getLocalizedSiteUrl(this.appConfig, siteUrl);
    if (!!ssoToken && page && page[IHG_PAGE_HEADER] && (typeof page[IHG_PAGE_HEADER][UHF_OPEN_ACCOUNT_MANAGEMENT] === 'function')) {
      page[IHG_PAGE_HEADER][UHF_OPEN_ACCOUNT_MANAGEMENT](siteUrlValue);
    } else {
      redirectToURL(this.appConfig.defaultDomain + siteUrlValue);
    }
  }

  bootstrap(statusChangeCallback: Function) {
    if (this.isUHFPresent()) {
      const page = this._window.nativeWindow[IHG_PAGE_NAMESPACE];
      if (page && page[IHG_PAGE_HEADER] && (typeof page[IHG_PAGE_HEADER][RELOAD_PAGE_OPTIONS] === 'function')) {
        page[IHG_PAGE_HEADER][RELOAD_PAGE_OPTIONS](this.appConfig.uhfReload, this.appConfig.appName);
        this._window.nativeWindow.$(this._window.nativeWindow.document).on(UHF_SIGN_STATE_CHANGE_EVENT, (event) => {
          const userStatus: UserStatusType = UserStatusType.fromString(useDefault(useDefault(event, {}).newValue, '').toUpperCase());
          if (userStatus) {
            statusChangeCallback(userStatus);
          }
        });
      }
    }
  }

  publishEnrollmentEvent() {
    const page = this._window.nativeWindow[IHG_PAGE_NAMESPACE];
    if (!this.isUHFPresent() || !page) {
      return;
    }
    this._window.nativeWindow.$(this._window.nativeWindow.document).trigger(UHF_LOGIN_CHANGE_EVENT);
  }

  getMemberFromProfile(defaultStatus: UserStatusType = UserStatusType.ANONYMOUS): Observable<User> {
    const page = this._window.nativeWindow[IHG_PAGE_NAMESPACE];
    if (!this.isUHFPresent() || !page) {
      return Observable.of(new User(defaultStatus));
    }
    return Observable.fromPromise(page[IHG_PAGE_HEADER][PAGE_USER_INFO])
      .map((userStatusData : any) => {
        const userStatus = UserStatusType.fromString(useDefault(userStatusData.status, {}));
        return new UserModelBuilder().withStatus(userStatus).withMemberPageData(page[IHG_PAGE_HEADER]).build();
      })
      .catch((error) => {
        return Observable.of(new User(defaultStatus));
      });
  }

  logInWithCredentials(memberNumber: string, pin: string, cookieFlag: boolean = true) {
    const page = this._window.nativeWindow[IHG_PAGE_NAMESPACE];
    if (this.isUHFPresent() && page) {
      Observable.fromPromise(page[IHG_PAGE_HEADER][UHF_DO_LOG_IN](memberNumber, pin, cookieFlag))
        .subscribe((data: any) => this.setToken(data));
    }
  }

  setToken(data:any){
    if (data && data.token) {
      this._cookieService.delete(IHG_SSO_TOKEN);
      if (this._window.nativeWindow.location.host.indexOf(IHG_CHINA_DOMAIN) > -1) {
        this._cookieService.set(IHG_SSO_TOKEN, data.token, undefined, '/', IHG_CHINA_DOMAIN);
      } else if (this._window.nativeWindow.location.host.indexOf(IHG_DOMAIN) > -1) {
        this._cookieService.set(IHG_SSO_TOKEN, data.token, undefined, '/', IHG_DOMAIN);
      }
      else {
        this._cookieService.set(IHG_SSO_TOKEN, data.token, undefined, '/');
      }
    }
    this.publishEnrollmentEvent();
  }

}



// WEBPACK FOOTER //
// ./src/main/webapp/stay-mgmt/app/api/services/uhf-interaction.service.ts